import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Stack, useTheme, useMediaQuery } from "@mui/material";
import Question from "../../components/Question";
import MoveButton from "../../components/MoveButton";
import CustomSpinner from "../../components/CustomSpinner";
import { callBackendApiGet } from "../../lib/api";
import { setMissingPatient, setReturning, setPatients, setSelectedAppointmentPatient, clearAppointmentToReschedule } from "../../redux/userSlice";
import logDrtEvent from '../../lib/logging';
import { useAuth0 } from "@auth0/auth0-react";

export default function AppointmentBookingPatients() {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);
    const location = useLocation();
    const { portal = false } = location.state || {};
    const { getAccessTokenSilently } = useAuth0();

    const [loading, setLoading] = useState(true);
    const hasFetchedRef = useRef(false);

    useEffect(() => {
        if (hasFetchedRef.current) return;

        if (!user.patients || user.patients.length === 0) {
            fetchPatientData();
        } else {
            setLoading(false);
        }

        hasFetchedRef.current = true;
    }, [user.patients]);

    const fetchPatientData = async () => {
        try {
            setLoading(true);
            const encodedPhoneNo = encodeURIComponent(user.phoneNo);
            let response = null;
            if (portal) {
                const token = await getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
                });
                response = await callBackendApiGet(`/api/v1/portal/patient?phone=${encodedPhoneNo}`, {}, token);
            } else {
                response = await callBackendApiGet(`/api/v1/ret-patients?q=${encodedPhoneNo}`);
            }
            
            if (response.status !== 200) {
                throw new Error(`Failed to fetch patients: ${response?.status}`);
            }

            const data = await response.json();

            // Handle both response formats:
            // 1. Portal API returns array directly: [patient1, patient2, ...]
            // 2. Non-portal API returns object: { returning_patients: [...], tellescope_patient_exists: boolean }
            const patients = Array.isArray(data) ? data : (data?.returning_patients || []);

            if (!Array.isArray(patients)) {
                throw new Error("Invalid response format: expected array of patients");
            }

            dispatch(setPatients(patients));

            if (patients.length === 1) {
                handlePatientSelection(patients[0]);
            } else if (patients.length === 0) {
                // Only check tellescope_patient_exists if it exists in the response (non-portal case)
                if (data?.tellescope_patient_exists) {
                    dispatch(setMissingPatient(true));
                    logDrtEvent(`[appointment] Patient started appointment booking but navigated to demographic screen to create a piction patient profile ( tellescope patient profile exists )`);
                    navigate("/demographic", { state: { returning: true } });
                } else {
                    logDrtEvent(`[appointment] Patient started appointment booking but navigated to concern screen to create a tellescope and piction patient profile`);
                    dispatch(setReturning(false));
                    navigate("/concern", { state: { returning: false } });
                }
            } else {
                logDrtEvent(`[appointment] Patient with more than 1 records`);
            }
        } catch (err) {
            logDrtEvent(`Error while fetching patients with phone number: ${err}`);
            navigate("/error", { state: { appointment: true } });
        } finally {
            setLoading(false);
        }
    };

    const handlePatientSelection = (patient) => {
        dispatch(setSelectedAppointmentPatient(patient));
        dispatch(clearAppointmentToReschedule());
        navigate('/appointment', { state: { portal } });
    };

    const handleBack = () => {
        if (portal) {
            navigate('/appointment-list', { state: { portal: portal } });
        } else {
            navigate('/appointment-booking');
        }
    };

    if (loading || !user.patients || user.patients.length === 0) {
        return <CustomSpinner />;
    }

    return (
        <Question
            onBack={handleBack}
            noBack={!portal}
            title="Select a patient"
            subtitle="Who is this appointment for?"
            input={
                <Box display="flex" flexDirection="column" alignItems="center" width="100%">
                    <Stack spacing={2} width="100%" maxWidth="sm" alignItems="center">
                        {user?.patients.map((patient, index) => (
                            <MoveButton
                                key={index}
                                onClick={() => handlePatientSelection(patient)}
                                title={patient.full_name}
                                selected={false}
                                sx={{ width: isDesktop ? "100%" : "100%", maxWidth: "400px" }}
                            />
                        ))}
                    </Stack>
                </Box>
            }
        />
    );
}
